/**
 * Audit Log Header Component
 * Includes page title, export button, and connection status
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Download, FileJson, FileText, FileSpreadsheet, WifiIcon, WifiOff } from 'lucide-react';
import { useAuditStore } from '@/stores/auditStore';
import { useToast } from '@/hooks/use-toast';

export const AuditHeader: React.FC = () => {
  const { exportLogs, isExporting, exportProgress, isConnected } = useAuditStore();
  const { toast } = useToast();

  const handleExport = async (format: 'csv' | 'json' | 'pdf') => {
    try {
      await exportLogs(format);
      toast({
        title: 'Export successful',
        description: `Audit logs exported as ${format.toUpperCase()}.`,
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: 'Failed to export audit logs. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <p className="text-muted-foreground">
          Track all AI actions and system events in real-time
        </p>
      </div>

      <div className="flex items-center gap-4">
        {/* Connection Status */}
        <div className="flex items-center gap-2">
          <div 
            data-testid="connection-indicator"
            className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} 
          />
          <span className="text-sm text-muted-foreground">
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {isConnected ? (
            <WifiIcon className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-gray-400" />
          )}
        </div>

        {/* Export Button */}
        {isExporting ? (
          <div className="flex items-center gap-2">
            <Progress value={exportProgress} className="w-32" />
            <span className="text-sm text-muted-foreground">
              Exporting... {exportProgress}%
            </span>
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>
                <FileJson className="mr-2 h-4 w-4" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileText className="mr-2 h-4 w-4" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};